import { useState } from 'react';
import { Link } from 'wouter';
import { toast } from 'sonner';
import { trpc } from '../lib/trpc';
import { translateError } from '../lib/errors';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageSpinner,
  StatCard,
} from '../components/ui';

// ─── Settings ───────────────────────────────────────────────────────────────
export function SettingsPage() {
  const { t } = useLocale();
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('settings.title')}</h1>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Link href="/settings/profile">
          <Card className="transition-colors hover:border-primary/40">
            <CardContent className="p-5">
              <p className="font-medium">{t('settings.profile')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.profileDescription')}</p>
            </CardContent>
          </Card>
        </Link>
        <Link href="/connections">
          <Card className="transition-colors hover:border-primary/40">
            <CardContent className="p-5">
              <p className="font-medium">{t('settings.connections')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.connectionsDescription')}</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}

export function SettingsProfilePage() {
  const { t } = useLocale();
  const { user, refetch } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const updateName = trpc.auth.updateProfile.useMutation({
    onSuccess: () => {
      toast.success(t('settings.toastProfileUpdated'));
      refetch();
    },
  });
  const changeEmail = trpc.auth.changeEmail.useMutation({
    onSuccess: () => {
      toast.success(t('settings.toastEmailChanged'));
      refetch();
    },
    onError: (e) => toast.error(translateError(e)),
  });
  const del = trpc.auth.deleteAccount.useMutation({
    onSuccess: () => {
      window.location.href = '/login';
    },
  });
  return (
    <div className="max-w-lg space-y-6">
      <h1 className="text-2xl font-semibold">{t('settings.profileTitle')}</h1>
      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="space-y-1.5">
            <Label>{t('settings.displayName')}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button
            size="sm"
            onClick={() => updateName.mutate({ name: name.trim() })}
            disabled={updateName.isPending || !name.trim()}
          >
            {t('settings.saveName')}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="space-y-1.5">
            <Label>{t('settings.loginEmail')}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={user?.loginMethod !== 'email'}
            />
          </div>
          {user?.loginMethod !== 'email' ? (
            <p className="text-xs text-muted-foreground">{t('settings.fbEmailInfo')}</p>
          ) : (
            <Button
              size="sm"
              onClick={() => changeEmail.mutate({ email: email.trim() })}
              disabled={changeEmail.isPending}
            >
              {t('settings.changeEmail')}
            </Button>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-3 p-5">
          <p className="font-medium text-destructive">{t('settings.dangerZone')}</p>
          <p className="text-sm text-muted-foreground">{t('settings.dangerDescription')}</p>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => {
              if (prompt(t('settings.deleteConfirmPrompt')) === 'DELETE') del.mutate({ confirm: 'DELETE' });
            }}
          >
            {t('settings.deleteAccount')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Webhook health ─────────────────────────────────────────────────────────
export function WebhookHealthPage() {
  const { t, relativeTime } = useLocale();
  const q = trpc.webhook.stats.useQuery();
  if (q.isLoading) return <PageSpinner />;
  const s = q.data!;
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('webhook.title')}</h1>
      <Card>
        <CardContent className="space-y-2 p-5 text-sm">
          <p>
            {t('webhook.callbackUrl')} <code className="rounded bg-muted px-1.5 py-0.5">{s.callbackUrl}</code>
          </p>
          <p>
            {t('webhook.verifyToken')}{' '}
            {s.verifyTokenConfigured ? (
              <Badge variant="success">{t('webhook.configured')}</Badge>
            ) : (
              <Badge variant="destructive">{t('webhook.missing')}</Badge>
            )}{' '}
            · {t('webhook.appSecret')}{' '}
            {s.appSecretConfigured ? (
              <Badge variant="success">{t('webhook.configured')}</Badge>
            ) : (
              <Badge variant="destructive">{t('webhook.missing')}</Badge>
            )}
          </p>
        </CardContent>
      </Card>
      <div className="grid gap-4 sm:grid-cols-4">
        <StatCard label={t('webhook.eventsReceived')} value={s.total} sub={t('webhook.today', { count: s.today })} />
        <StatCard label={t('webhook.processed')} value={s.processed} />
        <StatCard label={t('webhook.validSignature')} value={s.signatureValid} />
        <StatCard label={t('webhook.invalidSignature')} value={s.signatureInvalid} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t('webhook.recentEvents')}</CardTitle>
        </CardHeader>
        <CardContent>
          {!s.recent.length ? (
            <p className="text-sm text-muted-foreground">{t('webhook.emptyEvents')}</p>
          ) : (
            <div className="space-y-1.5 text-sm">
              {s.recent.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-md border px-3 py-2">
                  <span>
                    #{e.id} {e.object ?? ''}
                  </span>
                  <div className="flex items-center gap-2">
                    {e.signatureValid ? (
                      <Badge variant="success">{t('webhook.sigOk')}</Badge>
                    ) : (
                      <Badge variant="destructive">{t('webhook.sigBad')}</Badge>
                    )}
                    {e.processed ? (
                      <Badge variant="secondary">{t('webhook.processedBadge')}</Badge>
                    ) : (
                      <Badge variant="warning">{t('webhook.pendingBadge')}</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{relativeTime(e.receivedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
